/**
 * O balão de suporte no canto da tela (29/09/2026).
 *
 * Qualquer pessoa logada chama a equipe da Cliqfy por aqui, sem instalar nada:
 * a casca monta este componente, ele pergunta ao servidor se o suporte está
 * ligado (`GET /v1/suporte/chat`) e, se estiver, carrega o Web Chat do motor
 * de conversa — servido pelo nosso próprio domínio — e diz quem está chamando.
 *
 * A identidade vem ASSINADA pelo servidor (HMAC). O navegador só repassa: sem
 * o segredo do canal, ele não conseguiria se passar por outra pessoa.
 *
 * Ao sair (a casca desmonta), a conversa é esquecida neste navegador: quem
 * entrar depois no mesmo computador não herda a conversa de quem saiu.
 */

import { useEffect, useState, type ReactNode } from 'react';
import type { ChatDeSuporte } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { Icone } from '@/shared/ui';

interface ApiDoWidget {
  setUser: (
    identificador: string,
    dados: { name: string; email: string; identifier_hash: string },
  ) => void;
  setCustomAttributes: (atributos: Record<string, string>) => void;
  setLocale: (locale: string) => void;
  toggleBubbleVisibility: (visivel: 'show' | 'hide') => void;
  toggle: (estado?: 'open' | 'close') => void;
  reset: () => void;
}

declare global {
  interface Window {
    chatwootSettings?: Record<string, unknown>;
    chatwootSDK?: { run: (opcoes: { websiteToken: string; baseUrl: string }) => void };
    $chatwoot?: ApiDoWidget;
  }
}

const ID_DO_SCRIPT = 'cliqcrm-suporte-sdk';

/**
 * O item "Falar com o suporte" do menu lateral (v3, #677).
 *
 * O balão flutuante do widget saiu do canto da tela: ele cobria o conteúdo
 * e não era do produto. O que fica é um item do menu, acima do nome de quem
 * está logado, que abre a mesma conversa com o suporte. Sem suporte ativo,
 * o item não aparece — nada de botão que não leva a lugar nenhum.
 */
export function BalaoDeSuporte({ recolhido = false }: { recolhido?: boolean }): ReactNode {
  const api = useApi();
  const [disponivel, definirDisponivel] = useState(false);

  useEffect(() => {
    if (!api) return;
    let ativo = true;
    let aoFicarPronto: (() => void) | null = null;

    void api
      .chatDeSuporte()
      .then((chat) => {
        if (!ativo || !chat.ativo || !chat.website_token) return;
        aoFicarPronto = () => identificar(chat);
        window.addEventListener('chatwoot:ready', aoFicarPronto);
        carregar(chat.website_token);
        if (window.$chatwoot) identificar(chat);
        definirDisponivel(true);
      })
      // Sem suporte, sem item: não é erro de tela, e não tira nada de ninguém.
      .catch(() => undefined);

    return () => {
      ativo = false;
      if (aoFicarPronto) window.removeEventListener('chatwoot:ready', aoFicarPronto);
      window.$chatwoot?.reset();
      definirDisponivel(false);
    };
  }, [api]);

  if (!disponivel) return null;

  return (
    <button
      type="button"
      className="casca__item"
      {...(recolhido ? { title: 'Falar com o suporte' } : {})}
      onClick={() => window.$chatwoot?.toggle('open')}
    >
      <span className="casca__icone">
        <Icone nome="atendimento" />
      </span>
      <span className="casca__nome">Falar com o suporte</span>
    </button>
  );
}

function carregar(websiteToken: string): void {
  if (document.getElementById(ID_DO_SCRIPT)) return;

  const base = location.origin;
  window.chatwootSettings = {
    locale: 'pt_BR',
    position: 'right',
    type: 'standard',
    launcherTitle: 'Fale com o suporte',
    // O balão flutuante fica escondido: quem abre é o item do menu.
    hideMessageBubble: true,
  };

  const script = document.createElement('script');
  script.id = ID_DO_SCRIPT;
  script.src = `${base}/packs/js/sdk.js`;
  script.async = true;
  script.onload = () => window.chatwootSDK?.run({ websiteToken, baseUrl: base });
  document.body.appendChild(script);
}

function identificar(chat: ChatDeSuporte): void {
  const widget = window.$chatwoot;
  if (!widget || !chat.identificador || !chat.identificador_hash) return;
  widget.setUser(chat.identificador, {
    name: chat.nome,
    email: chat.email,
    identifier_hash: chat.identificador_hash,
  });
  widget.setCustomAttributes({ revenda: chat.revenda });
}
